import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, RotateCw } from 'lucide-react';
import { documentSignAssetUrl, getDocumentSignMeta, submitDocumentSignature } from '@/api/files';
import { describeApiError } from '@/components/reports/envelope/envelopeModel';
import { SignatureCaptureForm } from '@/components/signatures/SignatureCaptureForm';
import { SignPageShell, SignOutcomeCard, fmtDay } from '@/components/signatures/remote/SignPageShell';
import { DocumentSignHeader } from '@/components/signatures/remote/DocumentSignHeader';

// Loop D13 — only requests with placed fields load this chunk (and pdf.js behind it).
const FieldSignView = lazy(() => import(/* webpackChunkName: "docsign-fields-sign" */ '@/components/files/docsign/fields/FieldSignView'));

const CONTACT_LINE = 'Please contact the company that sent you this link.';
const DEAD_TITLES = {
  agreement_expired: 'This proposal has expired',
  link_expired: 'This signing link has expired',
  envelope_voided: 'This signature request was cancelled',
  link_invalid: 'This signing link isn’t valid',
};

/** Map a meta/submit error to the page state it lands on (null = retryable in place). Messages stay verbatim. */
export function phaseForError(err) {
  const e = describeApiError(err);
  if (e.status === 409 && e.code === 'already_signed') {
    return { kind: 'already_signed', signedAt: err.response.data.detail.signed_at };
  }
  if (e.status === 404 || e.status === 410) {
    return { kind: 'dead', title: DEAD_TITLES[e.code] || DEAD_TITLES.link_invalid, message: e.message };
  }
  return null;
}

const copyLine = (status, waiting) => {
  if (waiting) return 'Others still need to sign. We’ll email you a signed copy as soon as everyone has.';
  return status === 'stored'
    ? 'A signed copy of the document is on its way to your inbox.'
    : 'We’ll email you a signed copy of the document as soon as it’s ready.';
};

function Outcome({ phase, meta, onReload }) {
  if (phase.kind === 'dead') {
    return (
      <SignOutcomeCard tone="dead" title={phase.title} testId="doc-sign-dead-end">
        <p data-testid="doc-sign-dead-message">{phase.message}</p>
        <p>{CONTACT_LINE}</p>
        {phase.retry && (
          <button type="button" onClick={onReload} data-testid="doc-sign-reload" className="inline-flex items-center gap-1 text-xs font-semibold text-[#4575A8] hover:text-[#5C8AC2]">
            <RotateCw className="w-3.5 h-3.5" /> Try again
          </button>
        )}
      </SignOutcomeCard>
    );
  }
  const docName = meta?.document?.name;
  if (phase.kind === 'already_signed') {
    return (
      <SignOutcomeCard tone="done" title={phase.waiting ? 'You’ve signed. Waiting on the others' : "You've already signed"} testId="doc-sign-already-signed">
        <p>Your signature{docName ? ` on ${docName}` : ''} was recorded on{' '}
          <strong className="text-[#111417]" data-testid="doc-sign-already-signed-date">{fmtDay(phase.signedAt)}</strong>.</p>
        {phase.waiting
          ? <p data-testid="doc-sign-waiting-others">Others still need to sign. We&apos;ll email you a signed copy as soon as everyone has. There&apos;s nothing else you need to do.</p>
          : <p>There&apos;s nothing else you need to do.</p>}
      </SignOutcomeCard>
    );
  }
  return (
    <SignOutcomeCard tone="success" title="Thank you — you've signed" testId="doc-sign-success">
      <p>Your signature{docName ? ` on ${docName}` : ''} has been recorded.</p>
      <p data-testid="doc-sign-success-message">{copyLine(phase.copyStatus, phase.waiting)}</p>
    </SignOutcomeCard>
  );
}

/** Submit + the page transition it causes (success / already signed / dead end), or a retryable inline error. */
function useSignSubmit(token, onDone) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submit = async (payload) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await submitDocumentSignature(token, payload);
      onDone({ kind: 'success', copyStatus: res?.signed_copy?.status, waiting: !!res?.waiting_on_others });
    } catch (e) {
      const next = phaseForError(e);
      if (next) onDone(next);
      else setError(describeApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  };
  return { submit, submitting, error };
}

function SignStep({ meta, token, onDone }) {
  const { submit: send, submitting, error } = useSignSubmit(token, onDone);
  const submit = ({ name, data_url, signature_type }) => send({ name, data_url, signature_type });
  return (
    <section className="bg-white border border-[#D5D8DD] rounded-sm" data-testid="doc-sign-step">
      <header className="px-4 sm:px-6 py-3 border-b border-[#E6E8EC]">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6370]">Step 2</div>
        <h2 className="text-base font-bold text-[#111417]">Sign</h2>
      </header>
      <div className="p-4 sm:p-6 space-y-4">
        {error && (
          <div role="alert" data-testid="doc-sign-submit-error"
            className="p-3 text-sm rounded-sm border border-[#B4463A]/20 border-l-4 border-l-[#B4463A] bg-[#B4463A]/10 text-[#8C332A]">
            <p>{error}</p>
            <p className="text-xs mt-1">Your signature is still here. Tap Sign document to try again.</p>
          </div>
        )}
        <SignatureCaptureForm onSubmit={submit} submitting={submitting} submitLabel="Sign document"
          consentText={meta.consent_text} initialName={meta.signer?.name || ''} showEmail={false} />
      </div>
    </section>
  );
}

/** Loop D13 — a request with placed fields: pdf.js pages + sign-here tabs (lazy; field-less requests never load it). */
function FieldsStep({ meta, token, onDone }) {
  const sign = useSignSubmit(token, onDone);
  return (
    <Suspense fallback={<PageLoading testId="doc-sign-fields-chunk-loading" text="Loading your document…" />}>
      <FieldSignView meta={meta} {...sign} />
    </Suspense>
  );
}

function PageLoading({ testId, text }) {
  return (
    <div className="flex items-center justify-center gap-2 py-24 text-sm text-[#5C6370]" data-testid={testId}>
      <Loader2 className="w-4 h-4 animate-spin" /> {text}
    </div>
  );
}

/** Loop D8 — public `/sign/:token` page for a stored-PDF signature request. */
export default function DocumentSignPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [phase, setPhase] = useState({ kind: 'loading' });

  const load = useCallback(() => {
    let cancelled = false;
    setPhase({ kind: 'loading' });
    getDocumentSignMeta(token)
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        setPhase(m.state === 'already_signed' ? { kind: 'already_signed', signedAt: m.signed_at, waiting: !!m.waiting_on_others } : { kind: 'ready' });
      })
      .catch((e) => {
        if (cancelled) return;
        setPhase(phaseForError(e) || { kind: 'dead', retry: true, title: 'We couldn’t load this signing request', message: describeApiError(e).message });
      });
    return () => { cancelled = true; };
  }, [token]);
  useEffect(() => load(), [load]);

  return (
    <SignPageShell companyName={meta?.company?.name} logoUrl={meta?.company?.logo_url}>
      {phase.kind === 'loading' && <PageLoading testId="doc-sign-loading" text="Loading your signing request…" />}
      {phase.kind === 'ready' && meta && meta.fields?.length > 0 && <FieldsStep meta={meta} token={token} onDone={setPhase} />}
      {phase.kind === 'ready' && meta && !(meta.fields?.length > 0) && (
        <>
          <DocumentSignHeader meta={meta} pdfUrl={documentSignAssetUrl(meta.document_url)} />
          <SignStep meta={meta} token={token} onDone={setPhase} />
        </>
      )}
      {['dead', 'already_signed', 'success'].includes(phase.kind) && <Outcome phase={phase} meta={meta} onReload={load} />}
    </SignPageShell>
  );
}
